import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider, QueryCache } from "@tanstack/react-query";
import { toast, Toaster } from "sonner";
import i18n from "i18next";
import "./index.css";

import App from "./App.tsx";
import { AppWrapper } from "./components/common/PageMeta.tsx";
import { ThemeProvider } from "./context/ThemeContext.tsx";
import { ErrorBoundary } from "./components/common/ErrorBoundary.tsx";
import "./i18n";

import { PostHogProvider } from "@posthog/react";
import type { PostHogConfig } from "posthog-js";
import { POSTHOG_ENABLED } from "./config/posthog";

const posthogOptions = {
	api_host: import.meta.env.VITE_POSTHOG_HOST || "https://us.i.posthog.com",
	// Enables SPA pageviews on React Router navigation (history_change).
	defaults: "2025-05-24",
	// Anonymous visitors don't create person profiles until identify().
	person_profiles: "identified_only",
	// Business metrics are captured explicitly (see utils/analytics.ts);
	// autocapture is limited to clicks on buttons/links to keep volume low.
	autocapture: {
		dom_event_allowlist: ["click"],
		element_allowlist: ["button", "a"],
	},
	// Financial data: never record what users type.
	session_recording: { maskAllInputs: true },
} satisfies Partial<PostHogConfig>;

const queryClient = new QueryClient({
	queryCache: new QueryCache({
		// Global fallback for query errors. Hooks with a custom onError set
		// meta: { hasErrorHandler: true } to suppress this toast.
		onError: (_error, query) => {
			if (query.meta?.hasErrorHandler) return;
			toast.error(i18n.t("common:errors.loadFailed"));
		},
	}),
	defaultOptions: {
		queries: {
			retry: 1,
			refetchOnWindowFocus: false,
		},
		mutations: {
			// Global mutation error handler
			// Note: Individual mutations can override this with their own onError
			onError: (error: unknown) => {
				// Only log server errors (5xx) - already handled by axios interceptor
				// This is a fallback for any errors that aren't caught elsewhere
				if (
					error &&
					typeof error === "object" &&
					"response" in error &&
					(error as { response: { status: number } }).response.status >= 500
				) {
					console.error("[React Query] Uncaught server error:", error);
				}
				// Client errors (4xx) including validation errors (422) are handled in components
			},
		},
	},
});

const app = (
	<ThemeProvider>
		<AppWrapper>
			<ErrorBoundary>
				<App />
				<Toaster position="bottom-right" />
			</ErrorBoundary>
		</AppWrapper>
	</ThemeProvider>
);

createRoot(document.getElementById("root")!).render(
	<StrictMode>
		<QueryClientProvider client={queryClient}>
			{POSTHOG_ENABLED ? (
				<PostHogProvider
					apiKey={import.meta.env.VITE_POSTHOG_PROJECT_TOKEN}
					options={posthogOptions}
				>
					{app}
				</PostHogProvider>
			) : (
				app
			)}
		</QueryClientProvider>
	</StrictMode>,
);
